import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'

const BILLING_PORTAL_TIMEOUT_MS = 20000

const getStoredSupabaseAccessToken = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
  if (typeof window === 'undefined' || !supabaseUrl) return null

  try {
    const projectRef = new URL(supabaseUrl).hostname.split('.')[0]
    const authStorageValue = window.localStorage.getItem(`sb-${projectRef}-auth-token`)
    if (!authStorageValue) return null

    const session = JSON.parse(authStorageValue)
    return session?.access_token || session?.currentSession?.access_token || null
  } catch (error) {
    console.error('Unable to read Supabase session for billing portal:', error)
    return null
  }
}

/**
 * Subscription Management Modal Component
 * Shows current subscription details and management options
 * - Free users: See upgrade options
 * - Premium users: Manage billing through Stripe Customer Portal
 */
const SubscriptionManagementModal = ({ isOpen, onClose, onUpgrade }) => {
  const { user, userProfile, profileLoading, refreshUserProfile } = useAuth()
  const [isRetryingProfileSync, setIsRetryingProfileSync] = useState(false)
  const [isOpeningBillingPortal, setIsOpeningBillingPortal] = useState(false)
  const [billingPortalError, setBillingPortalError] = useState('')

  if (!isOpen) return null

  const isFree = userProfile?.tier === 'free'
  const isPremium = userProfile?.tier === 'premium'
  const isProfileUnavailable = !isFree && !isPremium

  const handleUpgrade = () => {
    onClose()
    onUpgrade()
  }

  const handleManageBilling = async () => {
    if (!user) {
      setBillingPortalError('Please log in to manage your billing.')
      return
    }

    try {
      setBillingPortalError('')
      setIsOpeningBillingPortal(true)

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
      const accessToken = getStoredSupabaseAccessToken()

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Billing portal is not configured. Missing Supabase environment variables.')
      }

      if (!accessToken) {
        throw new Error('Your session has expired. Please log in again and retry.')
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), BILLING_PORTAL_TIMEOUT_MS)
      const response = await fetch(`${supabaseUrl}/functions/v1/create-billing-portal-session`, {
        method: 'POST',
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      }).finally(() => clearTimeout(timeoutId))

      const data = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error(data?.error || `Unable to open billing portal. Server returned ${response.status}.`)
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      if (!data?.url) {
        throw new Error('Billing portal URL was not returned by the server.')
      }

      window.location.href = data.url
    } catch (error) {
      const message = error?.name === 'AbortError'
        ? 'Billing portal request timed out. Please try again.'
        : error?.message || 'Unable to open billing portal. Please try again.'
      console.error('Billing portal error:', {
        operation: 'create-billing-portal-session',
        message
      })
      setBillingPortalError(message)
      setIsOpeningBillingPortal(false)
    }
  }

  const handleRetryProfileSync = async () => {
    setIsRetryingProfileSync(true)
    await refreshUserProfile()
    setIsRetryingProfileSync(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-4">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-xl bg-white sm:max-w-2xl sm:rounded-2xl">
        
        {/* Header */}
        <div className="recipe-app-shell subscription-modal-header rounded-t-xl bg-recipe-gradient p-4 sm:rounded-t-2xl sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="mb-1 text-xl font-bold leading-tight text-white sm:mb-2 sm:text-3xl">
                Your Subscription
              </h2>
              <p className="text-sm text-white/90 sm:text-base">
                Manage your Yummtime account
              </p>
            </div>
            <button 
              onClick={onClose}
              className="rounded-full p-1.5 text-white transition-colors hover:bg-white/20 sm:p-2"
            >
              <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-6">
          
          {/* FREE USER VIEW */}
          {isFree && (
            <div>
              <Card className="mb-3 border-2 border-gray-200 sm:mb-6">
                <CardHeader className="p-4 text-center sm:p-6">
                  {/* <div className="text-5xl mb-3">🆓</div> */}
                  <CardTitle className="text-lg sm:text-2xl">Free Account</CardTitle>
                  <CardDescription className="mt-1 text-sm sm:mt-2 sm:text-lg">
                    You're currently on the free plan
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                  <div className="space-y-3 sm:space-y-4">
                    {/* Current Features */}
                    <div>
                      <h4 className="mb-1.5 text-base font-semibold text-gray-900 sm:mb-3 sm:text-xl">What you have:</h4>
                      <ul className="space-y-1.5 text-sm text-gray-600 sm:space-y-3 sm:text-lg">
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          Unlimited recipe searches
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          View up to 5 recipes per search
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          Ad-supported experience
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          Access to global recipes
                        </li>
                    
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          Full recipe information
                        </li>
                      </ul>
                    </div>

                    {/* What You're Missing */}
                    <div className="border-t pt-3 sm:pt-4">
                      <h4 className="mb-1.5 text-base font-semibold text-gray-900 sm:mb-3 sm:text-xl">Upgrade to get:</h4>
                      <ul className="space-y-1.5 text-sm text-gray-600 sm:space-y-3 sm:text-lg">
                       <li className="flex items-center">
                          <span className="mr-2 text-brand">⭐</span>
                          Unlimited recipe searches
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-brand">⭐</span>
                          100% Ad-free experience
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-brand">⭐</span>
                          View up to 15 recipes per search
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-brand">⭐</span>
                          Save up to 35 favorite recipes
                        </li>
                        {/* <li className="flex items-center">
                          <span className="text-brand mr-2">⭐</span>
                          Create recipe collections
                        </li> */}
                        <li className="flex items-center">
                          <span className="mr-2 text-brand">⭐</span>
                          Priority Support
                        </li>
                      </ul>
                    </div>

                    {/* Upgrade Button */}
                    <Button 
                      onClick={handleUpgrade}
                      className="nav-get-started-lines mt-3 h-10 w-full text-sm font-semibold sm:mt-6 sm:h-12 sm:text-lg"
                    >
                      <span className="relative z-10">Upgrade to Premium</span>
                    </Button>
                    
                    {/* Maybe Later Button */}
                    <Button 
                      onClick={onClose}
                      variant="outline"
                      className="mt-2 h-10 w-full text-sm font-semibold sm:mt-3 sm:h-12 sm:text-lg"
                    >
                      Maybe Later
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {isProfileUnavailable && (
            <Card className="mb-3 border-2 border-gray-200 sm:mb-6">
              <CardHeader className="p-4 text-center sm:p-6">
                <CardTitle className="text-lg sm:text-2xl">Account Sync</CardTitle>
                <CardDescription className="mt-1 text-sm sm:mt-2 sm:text-lg">
                  {profileLoading
                    ? 'Checking your current subscription...'
                    : 'Your subscription status is temporarily unavailable.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                <Button
                  onClick={handleRetryProfileSync}
                  disabled={profileLoading || isRetryingProfileSync}
                  className="mb-2 h-10 w-full text-sm font-semibold sm:mb-3 sm:h-12 sm:text-lg"
                >
                  {profileLoading || isRetryingProfileSync ? 'Checking...' : 'Retry Sync'}
                </Button>
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="h-10 w-full text-sm font-semibold sm:h-12 sm:text-lg"
                >
                  Close
                </Button>
              </CardContent>
            </Card>
          )}

          {/* PREMIUM USER VIEW */}
          {isPremium && (
            <div>
              <Card className="premium-subscription-card mb-3 border-2 border-transparent sm:mb-6 sm:border-4">
                <CardHeader className="bg-gradient-to-r from-brand/10 to-yellow-50 p-4 text-center sm:p-6">
                  <CardTitle className="text-lg text-brand sm:text-2xl">Premium Member</CardTitle>
                  <CardDescription className="mt-1 text-sm sm:mt-2 sm:text-lg">
                    Thank you for supporting It's Yummtime!
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-4 sm:p-6 sm:pt-6">
                  <div className="space-y-4 sm:space-y-6">
                    {/* Premium Features */}
                    <div>
                      <h4 className="mb-1.5 text-sm font-semibold text-gray-900 sm:mb-2 sm:text-base">Your Premium Benefits:</h4>
                      <ul className="space-y-1.5 text-sm text-gray-600 sm:space-y-2">
                      <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          Unlimited recipe searches
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          Access to global recipes
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          View up to 15 recipes per search
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          Full recipe information
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          Save up to 35 favorite recipes
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          100% ad-free experience
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2 text-green-500">✓</span>
                          Priority Support
                        </li>
                      </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2 border-t pt-3 sm:space-y-3 sm:pt-4">
                      {billingPortalError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 sm:text-sm">
                          {billingPortalError}
                        </div>
                      )}

                      <Button
                        onClick={handleManageBilling}
                        disabled={isOpeningBillingPortal}
                        className="nav-get-started-lines h-10 w-full text-sm sm:h-11 sm:text-base"
                      >
                        <span className="relative z-10">
                          {isOpeningBillingPortal ? 'Opening Stripe...' : 'Manage Billing'}
                        </span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

        </div>

        {/* Footer Info */}
        <div className="px-3 pb-3 sm:px-6 sm:pb-6">
          <p className="text-center text-xs text-gray-500 sm:text-base">
            Questions? Contact us at{' '}
            <a
              href="mailto:itsyummtime@sweetestinteractive.com"
              className="text-brand underline underline-offset-2 transition-colors hover:text-brand/80"
            >
              itsyummtime@sweetestinteractive.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionManagementModal
